import React, { useContext } from 'react';
import {
    ArcElement,
    Chart as ChartJS,
    Legend,
    Title,
    Tooltip
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Pie, Doughnut } from 'react-chartjs-2';
import { Colors } from '../../../Utils/styles/Colors';
import { I18nReactContext } from '../../../contexts/I18nContext';
import './PieChart.scss';

// Register Chart.js components
ChartJS.register(
    ArcElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

/**
 * Pie chart component using Chart.js for displaying question response data
 * Follows WeezBee's DoughnutChart patterns with integrated color system and Hebrew support
 * 
 * @param {object} params - Component parameters
 * @param {Array} params.data - Chart data array with labels and values
 * @param {string} [params.questionText] - The survey question text to display as title
 * @param {string} params.variant - Chart variant: 'pie' or 'doughnut' (default: 'pie')
 * @param {boolean} params.showPercentages - Whether to show percentage labels (default: true)
 * @param {boolean} params.excludeZeroValues - Whether to exclude zero values from display (default: false)
 * @param {boolean} params.showLegend - Whether to show the legend (default: true)
 * @param {number} params.height - Chart height in pixels (default: 300)
 * @param {string} [params.labelText] - Text to display as label (default: '')
 * @returns {JSX.Element} Rendered pie chart component
 */
export const PieChart = ({ 
    data, 
    questionText, 
    variant = 'pie',
    showPercentages = true,
    excludeZeroValues = false,
    showLegend = true,
    height = 300,
    labelText = ''
}) => {
    const { i18n } = useContext(I18nReactContext);

    // Consistent WeezBee color palette (same as bar chart)
    const colorPalette = [
        Colors["$GraphBackground1"], // Light green
        Colors["$GraphBackground2"], // Yellow
        Colors["$GraphBackground3"], // Light grey
        Colors["$GraphBackground4"], // Light purple
        Colors["$GraphBackground5"], // Light orange
        Colors["$GraphBackground6"], // Sky blue
        Colors["$GraphBackground7"], // Light pink
        Colors["$GraphBackground8"], // Powder blue
        Colors["$GraphBackground9"], // Peach
        Colors["$GraphBackground10"], // Plum
    ];

    // Filter out zero values if requested
    const filteredData = excludeZeroValues 
        ? data.filter(item => item.value > 0)
        : data;

    // Calculate total for percentage calculations
    const total = filteredData.reduce((sum, item) => sum + item.value, 0);

    // Calculate percentages for each data point
    const dataWithPercentages = filteredData.map(item => ({
        ...item,
        percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
    }));

    // Determine colors - use data object colors if provided, otherwise use palette
    const backgroundColors = dataWithPercentages.map((item, index) => {
        return item.color || colorPalette[index % colorPalette.length];
    });

    // Prepare data for Chart.js
    const chartData = {
        labels: dataWithPercentages.map(item => item.label) || [],
        datasets: [
            {
                label: String(questionText || i18n.translate('surveyCharts.tooltips.responses')),
                data: dataWithPercentages.map(item => item.value) || [],
                backgroundColor: backgroundColors,
                borderColor: Colors["$GraphBorder"],
                borderWidth: 2,
            }
        ]
    };

    // Chart.js options following WeezBee patterns
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 1,
        plugins: {
            legend: {
                display: showLegend,
                position: 'bottom',
                labels: {
                    font: {
                        family: 'Heebo',
                        size: 12
                    },
                    color: Colors["$pageTitle"],
                    padding: 15,
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                enabled: true,
                backgroundColor: Colors["$themeLight"],
                titleColor: Colors["$pageTitle"],
                bodyColor: Colors["$pageTitle"],
                borderColor: Colors["$GraphBorder"],
                borderWidth: 1,
                cornerRadius: 6,
                displayColors: true,
                titleFont: {
                    family: 'Heebo',
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    family: 'Heebo',
                    size: 13
                },
                callbacks: {
                    title: function(context) {
                        return context[0].label || '';
                    },
                    label: function(context) {
                        const value = context.raw || context.parsed;
                        const percentage = dataWithPercentages[context.dataIndex]?.percentage || 0;
                        const label = context.label || '';
                        return `${label}: ${value} ${labelText} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                display: showPercentages, 
                color: Colors["$pageTitle"],
                font: {
                    family: 'Heebo',
                    size: 12,
                    weight: 'bold',
                },
                formatter: (value, context) => {
                    const percentage = dataWithPercentages[context.dataIndex]?.percentage || 0;
                    // Only show percentage if slice is large enough to fit the label
                    return percentage >= 10 ? `${percentage}%` : '';
                },
                anchor: 'center',
                align: 'center',
                offset: 0,
                clip: true,
                textAlign: 'center',
                clamp: true,
            }
        },
        layout: {
            padding: {
                top: 5,
                bottom: 5,
                left: 5,
                right: 5
            }
        }
    };

    // Choose chart component based on variant
    const ChartComponent = variant === 'doughnut' ? Doughnut : Pie;

    return (
        <div className="pie-chart-container">
            <div 
                className="pie-chart-container__chart-wrapper" 
                style={{ height: `${height}px` }}
            >
                <ChartComponent 
                    data={chartData} 
                    options={options} 
                    plugins={showPercentages ? [ChartDataLabels] : []}
                />
            </div>
        </div>
    );
}; 